import { useMutation, useQuery } from '@tanstack/react-query'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { queryClient, trpc } from '../../utils/trpc'

export type RouterOutput = inferRouterOutputs<AppRouter>
export type RouterInput = inferRouterInputs<AppRouter>

export type TaskData = NonNullable<
	RouterOutput['client']['cycles']['reviewers']['searchAsParticipant' | 'searchAsManager']
>

type ReviewerGroup = {
	id: string
	name: string
	minReviewers: number
	maxReviewers: number
	optional?: boolean
	reviewers: Array<{
		id: string
		firstName: string
		lastName: string
		externalId: string
	}>
}

export function useReviewerSelection(isManagerView: boolean) {
	const { cycleId, taskId } = useParams<{
		cycleId: string
		taskId: string
	}>()
	const navigate = useNavigate()
	const { showToast } = useToast()
	const [editedReviewers, setEditedReviewers] = useState<
		Record<
			string,
			{
				id: string
				name: string
				reviewers: Array<{ externalId: string; firstName: string; lastName: string }>
			}
		>
	>({})

	const { data: taskData, isLoading: cycleReviewerLoading } = useQuery({
		...(isManagerView
			? trpc.client.cycles.reviewers.searchAsManager.queryOptions({
					cycleId: cycleId || '',
					taskId: taskId || '',
				})
			: trpc.client.cycles.reviewers.searchAsParticipant.queryOptions({
					cycleId: cycleId || '',
					taskId: taskId || '',
				})),
		enabled: !!cycleId && !!taskId,
	})

	useEffect(() => {
		if (taskData) {
			const reviewersByGroup = taskData.groups.reduce(
				(acc, group) => {
					acc[group.id] = {
						id: group.id,
						name: group.name,
						reviewers: group.reviewers,
					}
					return acc
				},
				{} as Record<
					string,
					{
						id: string
						name: string
						reviewers: Array<{ externalId: string; firstName: string; lastName: string }>
					}
				>,
			)
			setEditedReviewers(reviewersByGroup)
		}
	}, [taskData])

	const updateReviewersMutation = useMutation({
		...(isManagerView
			? trpc.client.cycles.reviewers.editAsManager.mutationOptions({
					onSuccess: () => {
						showToast('Raters updated successfully')
						const actualQueryKey = trpc.client.cycles.reviewers.searchAsManager.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
						queryClient.invalidateQueries({
							queryKey: actualQueryKey,
						})
					},
					onError: () => {
						showToast('Failed to update raters', 'error')
					},
				})
			: trpc.client.cycles.reviewers.editAsParticipant.mutationOptions({
					onSuccess: () => {
						showToast('Raters updated successfully')
						const actualQueryKey = trpc.client.cycles.reviewers.searchAsParticipant.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
						queryClient.invalidateQueries({
							queryKey: actualQueryKey,
						})
					},
					onError: () => {
						showToast('Failed to update raters', 'error')
					},
				})),
	})

	const submitReviewersMutation = useMutation({
		...trpc.client.cycles.reviewers.submit.mutationOptions({
			onSuccess: () => {
				showToast('Raters submitted successfully')
				const actualQueryKey = isManagerView
					? trpc.client.cycles.reviewers.searchAsManager.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
					: trpc.client.cycles.reviewers.searchAsParticipant.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
				queryClient.invalidateQueries({
					queryKey: actualQueryKey,
				})
				navigate('/dashboard')
			},
			onError: () => {
				showToast('Failed to submit raters', 'error')
			},
		}),
	})

	const approveReviewersMutation = useMutation({
		...trpc.client.cycles.reviewers.approve.mutationOptions({
			onSuccess: () => {
				showToast('Raters approved successfully')
				const actualQueryKey = isManagerView
					? trpc.client.cycles.reviewers.searchAsManager.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
					: trpc.client.cycles.reviewers.searchAsParticipant.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
				queryClient.invalidateQueries({
					queryKey: actualQueryKey,
				})
				navigate('/dashboard')
			},
			onError: () => {
				showToast('Failed to approve raters', 'error')
			},
		}),
	})

	const rejectReviewersMutation = useMutation({
		...trpc.client.cycles.reviewers.reject.mutationOptions({
			onSuccess: () => {
				showToast('Raters rejected successfully')
				const actualQueryKey = isManagerView
					? trpc.client.cycles.reviewers.searchAsManager.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
					: trpc.client.cycles.reviewers.searchAsParticipant.queryOptions({
							cycleId: cycleId || '',
							taskId: taskId || '',
						}).queryKey
				queryClient.invalidateQueries({
					queryKey: actualQueryKey,
				})
				navigate('/dashboard')
			},
			onError: () => {
				showToast('Failed to reject reviewers', 'error')
			},
		}),
	})

	const handleApproveReviewers = () => {
		approveReviewersMutation.mutate({
			cycleId: cycleId || '',
			taskId: taskId || '',
		})
	}

	const handleRejectReviewers = () => {
		rejectReviewersMutation.mutate({
			cycleId: cycleId || '',
			taskId: taskId || '',
		})
	}

	const hasChanges = useMemo(() => {
		if (!taskData) return false

		return taskData.groups.some((group) => {
			const getEditedReviewersForGroup = (group: ReviewerGroup) => group.reviewers || []
			const currentReviewers = getEditedReviewersForGroup(group)
			const editedGroupReviewers = editedReviewers[group.id]?.reviewers || []

			if (currentReviewers.length !== editedGroupReviewers.length) return true

			const currentIds = new Set(currentReviewers.map((r) => r.externalId))
			return editedGroupReviewers.some((r) => !currentIds.has(r.externalId))
		})
	}, [taskData, editedReviewers])

	const getTotalReviewersForGroup = useCallback(
		(group: ReviewerGroup) => editedReviewers[group.id]?.reviewers.length || group.reviewers?.length || 0,
		[editedReviewers],
	)

	const validateReviewersByGroups = useCallback((): {
		hasErrors: boolean
		errors: Record<string, string>
	} => {
		if (!taskData) return { hasErrors: false, errors: {} }

		const errors: Record<string, string> = {}

		// Calculate cumulative min/max only for required (non-optional) groups
		const cumulativeMinReviewers = Object.values(editedReviewers).reduce((sum, group) => {
			const groupMeta = taskData.groups.find((g) => g.id === group.id)
			// Only count minReviewers for non-optional groups
			return sum + (groupMeta && !groupMeta.optional ? groupMeta.minReviewers : 0)
		}, 0)

		const cumulativeMaxReviewers = Object.values(editedReviewers).reduce(
			(sum, group) => sum + (taskData.groups.find((g) => g.id === group.id)?.maxReviewers || 0),
			0,
		)

		const totalReviewers = Object.values(editedReviewers).reduce((sum, group) => sum + group.reviewers.length, 0)

		Object.values(editedReviewers).forEach((group) => {
			const groupMeta = taskData.groups.find((g) => g.id === group.id)
			if (!groupMeta) return

			const reviewersCount = group.reviewers.length

			// Skip minimum validation for optional groups
			if (reviewersCount < groupMeta.minReviewers && !groupMeta.optional) {
				errors[group.id] = `Min raters not met (${reviewersCount}/${groupMeta.minReviewers}) `
			}

			// Still enforce maximum for all groups (optional or not)
			if (reviewersCount > groupMeta.maxReviewers) {
				errors[group.id] = `Max raters exceeded (${reviewersCount}/${groupMeta.maxReviewers}) `
			}
		})

		if (totalReviewers < cumulativeMinReviewers) {
			Object.keys(editedReviewers).forEach((groupId) => {
				const groupMeta = taskData.groups.find((g) => g.id === groupId)
				// Only show global min if this group doesn't have its own minReviewers
				if (groupMeta && (groupMeta.minReviewers === 0 || groupMeta.optional)) {
					errors[groupId] = `${errors[groupId] || ''} Global min not met (${totalReviewers}/${cumulativeMinReviewers}) `
				}
			})
		}

		if (totalReviewers > cumulativeMaxReviewers) {
			Object.keys(editedReviewers).forEach((groupId) => {
				const groupMeta = taskData.groups.find((g) => g.id === groupId)
				// Only show global max if this group doesn't have its own maxReviewers
				if (groupMeta && groupMeta.maxReviewers === 0) {
					errors[groupId] =
						`${errors[groupId] || ''} Global max exceeded (${totalReviewers}/${cumulativeMaxReviewers}) `
				}
			})
		}

		return { hasErrors: Object.keys(errors).length > 0, errors }
	}, [taskData, editedReviewers])

	const startEditing = useCallback(() => {
		if (!taskData) return

		const initial: Record<
			string,
			{
				id: string
				name: string
				reviewers: Array<{ externalId: string; firstName: string; lastName: string }>
			}
		> = {}
		taskData.groups.forEach((group) => {
			initial[group.id] = {
				id: group.id,
				name: group.name,
				reviewers:
					group.reviewers?.map((r) => ({
						externalId: r.externalId,
						firstName: r.firstName,
						lastName: r.lastName,
					})) || [],
			}
		})

		setEditedReviewers(initial)
	}, [taskData])

	const cancelEditing = useCallback(() => {
		startEditing()
	}, [startEditing])

	const handleSaveReviewers = useCallback(async () => {
		if (!taskData || !hasChanges) return

		const validation = validateReviewersByGroups()
		if (validation.hasErrors) {
			Object.values(validation.errors).forEach((error) => {
				showToast(error, 'error')
			})
			return
		}

		const updatedReviewers = Object.entries(editedReviewers).flatMap(([groupId, group]) =>
			group.reviewers.map((reviewer) => ({
				reviewerExternalId: reviewer.externalId,
				reviewerGroupId: groupId,
			})),
		)

		updateReviewersMutation.mutate({
			cycleId: cycleId || '',
			taskId: taskId || '',
			reviewers: updatedReviewers,
		})
	}, [
		taskData,
		hasChanges,
		editedReviewers,
		cycleId,
		taskId,
		validateReviewersByGroups,
		updateReviewersMutation,
		showToast,
	])

	const handleSubmitReviewers = useCallback(async () => {
		if (!taskData) return

		const validation = validateReviewersByGroups()
		if (validation.hasErrors) {
			Object.values(validation.errors).forEach((error) => {
				showToast(error, 'error')
			})
			return
		}

		submitReviewersMutation.mutate({
			cycleId: cycleId || '',
			taskId: taskId || '',
		})
	}, [taskData, cycleId, taskId, validateReviewersByGroups, submitReviewersMutation, showToast])

	const addReviewer = useCallback(
		(groupId: string, reviewer: { id: string; firstName: string; lastName: string; externalId: string }) => {
			const formattedReviewer = {
				externalId: reviewer.externalId,
				firstName: reviewer.firstName,
				lastName: reviewer.lastName,
			}

			setEditedReviewers((prev) => ({
				...prev,
				[groupId]: {
					...prev[groupId],
					reviewers: [...(prev[groupId]?.reviewers || []), formattedReviewer],
				},
			}))
		},
		[],
	)

	return {
		taskData,
		cycleId,
		isLoading: cycleReviewerLoading,
		hasChanges,
		getTotalReviewersForGroup,
		validateReviewersByGroups,
		startEditing,
		cancelEditing,
		addReviewer,
		handleSaveReviewers,
		handleSubmitReviewers,
		handleApproveReviewers,
		handleRejectReviewers,
		editedReviewers,
		setEditedReviewers,
	}
}
